import React, {FC} from "react";
import {
  History,
  NestedOptionResponseItem,
  OptionResponseItem,
} from "ts/api/task";
import {IGetOneByIdTaskOption} from "@evennt-tech/api-v2-external";
import type {CheckboxesTaskLike} from "../CheckboxesResponse";
import useStyles from "./styles";

/**
 * Two modes: when `options` is set, match `multipleChoice` against
 * option ids and render the name plus any nested child. Otherwise walk
 * `task.optionsResponse` + `multipleChoiceIncludedChoices`.
 */
type Props = {
  historyItem?: History;
  multipleChoice: string;
  task?: CheckboxesTaskLike;
  options?: IGetOneByIdTaskOption[];
};
const renderMultiChoiceAnswer = (
  multiChoiceResponse: OptionResponseItem,
  nestedOption?: NestedOptionResponseItem
) => {
  const classes = useStyles();

  return (
    <>
      {multiChoiceResponse?.name}
      {nestedOption && (
        <ul className={classes.list}>
          <li key={nestedOption.key} className={classes.item}>
            {nestedOption.name}
          </li>
        </ul>
      )}
    </>
  );
};

const MultipleChoiceResponse: FC<Props> = ({
  task,
  historyItem,
  multipleChoice,
  options,
}) => {
  // The stored id is the deepest pick: for a nested selection we walk
  // `parentOptionId` back up to render the "Parent → Child" breadcrumb;
  // for a top-level pick `parentOptionId` is null and we render it alone.
  if (options) {
    const chosen = options.find((o) => o.id === multipleChoice);
    if (!chosen) {
      return <>{multipleChoice}</>;
    }
    const parent = chosen.parentOptionId
      ? options.find((o) => o.id === chosen.parentOptionId)
      : null;
    if (parent) {
      const primary: OptionResponseItem = {
        key: parent.id,
        name: parent.name,
      } as OptionResponseItem;
      const nested: NestedOptionResponseItem = {
        key: chosen.id,
        name: chosen.name,
      } as NestedOptionResponseItem;
      return renderMultiChoiceAnswer(primary, nested);
    }
    const primary: OptionResponseItem = {
      key: chosen.id,
      name: chosen.name,
    } as OptionResponseItem;
    return renderMultiChoiceAnswer(primary, undefined);
  }
  const multiChoiceHistoryResponse = historyItem?.optionsResponse?.find(
    (item) => item.key === multipleChoice
  );

  if (multiChoiceHistoryResponse) {
    const nestedOptionFromHistory = multiChoiceHistoryResponse?.nestedOptions?.find(
      (option) =>
        task?.multipleChoiceIncludedChoices &&
        task?.multipleChoiceIncludedChoices[multipleChoice]?.includes(
          option.key
        )
    );
    return renderMultiChoiceAnswer(
      multiChoiceHistoryResponse,
      nestedOptionFromHistory
    );
  }

  const multiChoiceResponse = task?.optionsResponse?.find(
    (item) => item.key === multipleChoice
  );

  if (multiChoiceResponse) {
    const nestedOption = multiChoiceResponse?.nestedOptions?.find(
      (option) =>
        task?.multipleChoiceIncludedChoices &&
        task?.multipleChoiceIncludedChoices[multipleChoice]?.includes(
          option.key
        )
    );
    return renderMultiChoiceAnswer(multiChoiceResponse, nestedOption);
  }

  return <>{multipleChoice}</>;
};

export default MultipleChoiceResponse;
